import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Loader2, MapPin, Sparkles, X, ChevronDown } from 'lucide-react';
import { AddressFieldsAutosuggest } from './auth/AddressFieldsAutosuggest';
import { toast } from 'sonner';

const SNOOZE_KEY = 'aga_address_nudge_snoozed';

/**
 * Soft inline onboarding nudge — replaces the old blocking modal.
 * Renders nothing when the user already has a complete address OR when
 * the user has snoozed it for the session. Auto-disappears the moment
 * the address is saved (refreshUser flips `address_required` to false).
 */
export function AddressOnboardingCard() {
  const { user, api, refreshUser } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [snoozed, setSnoozed] = useState(() => {
    try { return sessionStorage.getItem(SNOOZE_KEY) === '1'; }
    catch (_) { return false; }
  });
  const [address, setAddress] = useState({
    address_line: '', city: '', state: '', country: '',
    country_code: '', state_id: null, city_id: null, postal_code: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset snooze on logout / user switch
  useEffect(() => {
    if (!user) {
      try { sessionStorage.removeItem(SNOOZE_KEY); } catch (_) {/* ignore */}
      setSnoozed(false);
      setExpanded(false);
    }
  }, [user]);

  if (!user || !user.address_required || snoozed) return null;

  const submit = async () => {
    if (!address.address_line.trim()) { toast.error('Street address is required'); return; }
    if (!address.country || !address.country_code) { toast.error('Please select your country'); return; }
    if (!address.state.trim()) { toast.error('Please select your state / province'); return; }
    if (!address.city.trim()) { toast.error('Please select your city'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/auth/me/address', {
        address_line: address.address_line,
        city: address.city,
        state: address.state,
        country: address.country,
        country_code: address.country_code,
        state_id: address.state_id || null,
        city_id: address.city_id || null,
        postal_code: address.postal_code || null,
      });
      toast.success('Address saved — unlocking personalised picks…');
      try { sessionStorage.removeItem(SNOOZE_KEY); } catch (_) {/* ignore */}
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save address');
    }
    setSubmitting(false);
  };

  const snooze = () => {
    try { sessionStorage.setItem(SNOOZE_KEY, '1'); } catch (_) {/* ignore */}
    setSnoozed(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#00E5FF]/30 bg-[#041A3B] p-4 sm:p-5 mb-6"
      data-testid="address-onboarding-card"
    >
      {/* Subtle accent stripe along the left edge — replaces the wash-out gradient */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00E5FF] to-[#007AFF]" />
      {/* Decorative sparkle */}
      <Sparkles className="absolute right-3 top-3 w-5 h-5 text-[#00E5FF]/30" />

      <button
        type="button"
        onClick={snooze}
        className="absolute right-2 top-2 w-7 h-7 rounded-full flex items-center justify-center text-[#A0B4CC] hover:text-white hover:bg-white/5 z-10"
        aria-label="Dismiss"
        data-testid="address-onboarding-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 pl-3">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#00E5FF]" />
        </div>
        <div className="flex-1 min-w-0 pr-8 sm:pr-0">
          <h3 className="font-outfit text-base sm:text-lg font-semibold text-white">
            Add your address to unlock <span className="text-[#00E5FF]">3 personalised picks</span>
          </h3>
          <p className="text-[#A0B4CC] text-xs sm:text-sm mt-0.5 leading-relaxed">
            We use it to match you with the closest merchants and exclusive deals near you.
          </p>
        </div>
        {!expanded && (
          <Button
            onClick={() => setExpanded(true)}
            className="btn-primary h-10 sm:h-11 px-4 sm:px-5 w-full sm:w-auto"
            data-testid="address-onboarding-expand"
          >
            Add address
            <ChevronDown className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <AddressFieldsAutosuggest value={address} onChange={setAddress} required />
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              onClick={submit}
              disabled={submitting}
              className="btn-primary flex-1 h-11"
              data-testid="address-onboarding-save"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : 'Save & Unlock'}
            </Button>
            <Button
              onClick={() => setExpanded(false)}
              variant="outline"
              className="border-white/20 text-[#A0B4CC] hover:text-white hover:bg-white/5 h-11"
              data-testid="address-onboarding-collapse"
            >
              Maybe later
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddressOnboardingCard;
